// Billing B2.1a: the phone field fills "(   )    -    " as the booker types.
// Display only. On "Book", toE164 (phone.js) reads the value and the server
// checks it again, so a bug here can only show a wrong format, never save one.
// Plan: project files, billing/B2.1a-phone-mask-plan.md.

import { DEFAULT_COUNTRY_CODE } from './phone';

// The US pattern. 0 = one digit slot.
const US_PATTERN = '(000) 000-0000';
const US_DIGITS = 10;
// E.164 allows at most 15 digits after the +.
const MAX_INTL_DIGITS = 15;

// Invisible characters that copies from contacts apps can carry: soft hyphen,
// zero-width spaces and joiners, direction marks, BOM.
const INVISIBLE = /[\u00AD\u200B-\u200F\u202A-\u202E\u2060-\u2069\uFEFF]/;
// Hyphens and dashes from Word, Contacts or WhatsApp → '-'. Odd spaces → ' '.
const DASHES = /[‐-―−﹘﹣－]/;
const SPACES = /[  -   　]/;
// Only the characters people type in phone numbers (same set as toE164).
const PHONE_CHARS = /^\+?[\d\s().-]*$/;

const digitsOf = (text) => text.replace(/\D/g, '');

// Drops invisible characters and maps odd dashes and spaces. Keeps the caret
// on the same character.
function clean(text, caret) {
  let out = '';
  let newCaret = 0;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (!INVISIBLE.test(ch)) out += DASHES.test(ch) ? '-' : SPACES.test(ch) ? ' ' : ch;
    if (i < caret) newCaret = out.length;
  }
  return { text: out, caret: caret >= text.length ? out.length : newCaret };
}

// Caret goes right after the n-th digit of text (n = 0 → start).
function caretAfterDigits(text, n) {
  if (n <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < text.length; i++) {
    if (/\d/.test(text[i]) && ++seen === n) return i + 1;
  }
  return text.length;
}

// '8015550' → '(801) 555-0'. A separator shows only when a digit follows it.
function formatUs(national) {
  const a = national.slice(0, 3);
  const b = national.slice(3, 6);
  const c = national.slice(6);
  if (!national) return '';
  if (!b) return `(${a}`;
  if (!c) return `(${a}) ${b}`;
  return `(${a}) ${b}-${c}`;
}

/**
 * Formats a phone field value.
 * - text, caret: the field's new value and caret position.
 * - prevText: the value before this change. Backspace on a ')' or '-' then
 *   deletes the digit before it, so the booker is never stuck on a symbol.
 * - deleteForward: true for the Delete key (deletes the digit after).
 * - intlFormat: optional (digits with a +) → formatted text, for numbers
 *   outside the US. Loaded only when needed (PhoneInput).
 * Returns { text, caret, mode, ghost }:
 * - mode: 'us' (pattern), 'intl' (other country, + first), 'raw' (as typed).
 * - ghost: the rest of the US pattern, drawn after the text; '' if none.
 * Never drops a character the booker typed: unknown input → 'raw'.
 */
export function formatPhone(text, caret = text.length, { prevText = '', deleteForward = false, intlFormat } = {}) {
  ({ text, caret } = clean(String(text ?? ''), caret));
  const raw = { text, caret, mode: 'raw', ghost: '' };

  // A separator was deleted: delete the digit next to it instead.
  const prev = clean(prevText, prevText.length).text;
  if (text.length < prev.length && digitsOf(text) === digitsOf(prev) && digitsOf(text)) {
    const before = text.slice(0, caret);
    const after = text.slice(caret);
    if (deleteForward) {
      const i = after.search(/\d/);
      if (i >= 0) text = before + after.slice(0, i) + after.slice(i + 1);
    } else {
      const i = before.search(/\d\D*$/);
      if (i >= 0) {
        text = before.slice(0, i) + before.slice(i + 1) + after;
        caret = i;
      }
    }
  }

  if (!PHONE_CHARS.test(text.trim())) return raw;
  const plus = text.trim().startsWith('+');
  const digits = digitsOf(text);
  const digitsBeforeCaret = digitsOf(text.slice(0, caret)).length;
  const done = (out, mode, ghost = '') => ({ text: out, caret: caretAfterDigits(out, digitsBeforeCaret), mode, ghost });

  // Another country: its own rules, or as typed until they load.
  if (plus && digits && !digits.startsWith(DEFAULT_COUNTRY_CODE)) {
    if (digits.length > MAX_INTL_DIGITS || !intlFormat) return { ...raw, text, caret, mode: 'intl' };
    return done(intlFormat(`+${digits}`), 'intl');
  }
  if (plus && !digits) return { ...raw, text, caret };

  // US: '+1 ', '1 ' or nothing before the 10 digits.
  const hasCode = digits.startsWith(DEFAULT_COUNTRY_CODE);
  const prefix = hasCode ? `${plus ? '+' : ''}${DEFAULT_COUNTRY_CODE} ` : '';
  const national = hasCode ? digits.slice(DEFAULT_COUNTRY_CODE.length) : digits;
  if (national.length > US_DIGITS) return { ...raw, text, caret };

  const pattern = prefix + US_PATTERN;
  const out = national ? prefix + formatUs(national) : prefix.trimEnd();
  return done(out, 'us', pattern.slice(out.length));
}
